"use client";
import Image from "next/image";
import React from "react";

interface FeeItem {
  label: string;
  amount: string;
}

const feeItems: FeeItem[] = [
  {
    label: "CPA Study Materials Published by McGraw Hill",
    amount: "₹49,000",
  },
  {
    label: "CPA Eligibility, Evaluation & License Support Fees",
    amount: "₹10,000",
  },
  {
    label: "Public Accounting Training Program Fees",
    amount: "₹40,000",
  },
];

const additionalFees = [
  "Foreign Academic Credential Evaluation payable to U.S. Agency - ~$400 one-time",
  "CPA Exam Fee payable to U.S. State Board - ~$873 per exam (includes Exam Application Fees of ~$100, Exam Section Fees of ~$263 and International Testing Fees of $510)",
];

const gradientBg =
  "linear-gradient(to bottom right, rgba(51,153,255,0.44) 0%, rgba(51,153,255,0.00) 50%) bottom right / 50% 50% no-repeat, linear-gradient(to bottom left, rgba(51,153,255,0.44) 0%, rgba(51,153,255,0.00) 50%) bottom left / 50% 50% no-repeat, linear-gradient(to top left, rgba(51,153,255,0.44) 0%, rgba(51,153,255,0.00) 50%) top left / 50% 50% no-repeat, linear-gradient(to top right, rgba(51,153,255,0.44) 0%, rgba(51,153,255,0.00) 50%) top right / 50% 50% no-repeat, linear-gradient(to bottom right, rgba(51,153,255,0.26) 0%, rgba(51,153,255,0.00) 50%) bottom right / 50% 50% no-repeat, linear-gradient(to bottom left, rgba(51,153,255,0.26) 0%, rgba(51,153,255,0.00) 50%) bottom left / 50% 50% no-repeat, linear-gradient(to top left, rgba(51,153,255,0.26) 0%, rgba(51,153,255,0.00) 50%) top left / 50% 50% no-repeat, linear-gradient(to top right, rgba(51,153,255,0.26) 0%, rgba(51,153,255,0.00) 50%) top right / 50% 50% no-repeat, linear-gradient(180deg, rgba(51,153,255,0.02) 0%, rgba(51,153,255,0.20) 100%), #FFF";

const FeeCard = ({ label, amount }: FeeItem) => (
  <div className="rounded-[8px] bg-[#F9FAFB] p-4 flex flex-col items-center gap-2 w-full">
    <p className="text-[#101828] text-sm md:text-sm font-medium text-center">
      {label}
    </p>
    <p className="text-[#3399FF] text-xl md:text-2xl font-bold text-center">
      {amount}
    </p>
  </div>
);

const AdditionalFeesCard = () => (
  <div className="rounded-xl bg-[#F9FAFB] p-5 w-full">
    <p className="text-black text-sm md:text-base font-bold mb-2">
      #Additional Fees applicable for CPA exam-takers:
    </p>
    <ul className="list-disc pl-5 flex flex-col gap-2">
      {additionalFees.map((line, idx) => (
        <li
          key={idx}
          className="text-black text-xs md:text-sm leading-relaxed font-medium"
        >
          {line}
        </li>
      ))}
    </ul>
  </div>
);

const PricingBox = () => (
  <div
    className="rounded-lg relative overflow-hidden pt-[52px] px-4 pb-4 w-full"
    style={{ background: gradientBg }}
  >
    <div className="absolute top-0 left-0 rounded-tl-lg rounded-tr-none rounded-br-[17.351px] rounded-bl-none bg-[#ba1a1a] w-[56.287px] h-[34.703px] flex items-center justify-center gap-0.5 px-1.5 py-1">
      <span className="text-white text-[25.392px] font-bold leading-normal tracking-[-0.186px]">
        50
      </span>
      <span className="text-white text-[8.464px] font-bold leading-normal tracking-[-0.186px] text-left">
        %<br />
        OFF
      </span>
    </div>
    <div className="flex items-stretch gap-4 w-full max-md:flex-col">
      <div className="flex-1 rounded-lg bg-[#f9fafb] p-3 flex flex-col items-center gap-1">
        <p className="text-[#101828] text-sm font-medium leading-[27px] tracking-[-0.439px] text-center">
          Discounted CAIRA Fees
        </p>
        <p className="text-[#3399ff] text-lg font-bold leading-7 tracking-[-0.439px] text-center">
          ₹50,000
        </p>
      </div>
      <div className="flex-1 rounded-lg bg-[#f9fafb] p-3 flex flex-col items-center gap-1">
        <p className="text-[#101828] text-sm font-medium leading-[27px] tracking-[-0.439px] text-center">
          Bundle Fee for CPA + CAIRA
        </p>
        <p className="text-[#3399ff] text-lg font-bold leading-7 tracking-[-0.439px] text-center">
          ₹1,49,000
        </p>
      </div>
    </div>
  </div>
);

const PayBadge = () => (
  <div className="flex flex-col items-center gap-2">
    <Image
      src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cpa-new/cpa_badge_new.webp"
      alt="Pay ₹9,917/month"
      width={260}
      height={259}
      className="w-[260px] h-[259px] object-contain"
    />
    <p className="text-black text-[10px] font-medium leading-normal md:text-right text-center">
      *12-month EMI (zero interest, zero processing fees)
      <br />
      *Upfront Payment ₹30,000
    </p>
  </div>
);

const FeesSection = () => {
  return (
    <section className="py-[60px] bg-no-repeat bg-cover bg-center bg-[url('https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma-new/fees-bg-desk.webp')] max-md:p-4 max-md:bg-none max-md:bg-[#f3f3f3]">
      <div className="main_container flex flex-col items-center max-md:bg-white max-md:rounded-2xl max-md:px-4 max-md:py-6 max-md:w-full">
        <h2 className="text-center text-[32px] font-bold text-[#1d1d1d] m-0 mb-8 tracking-[-0.03em] max-sm:text-2xl max-sm:mb-4">
          CPA Exam <span className="text-[#3399FF]">Fees</span>
        </h2>

        {/* ── DESKTOP ── */}
        <div className="flex items-start justify-center gap-6 w-full max-md:hidden">
          {/* Left: stacked fee cards */}
          <div className="flex flex-col gap-4 flex-none w-[492px]">
            {feeItems.map((item) => (
              <FeeCard key={item.label} {...item} />
            ))}
            <AdditionalFeesCard />
          </div>

          {/* Right: pricing + pay */}
          <div className="flex flex-col items-center gap-6 flex-none w-[492px]">
            <PricingBox />
            <PayBadge />
          </div>
        </div>

        {/* ── MOBILE ── */}
        <div className="hidden max-md:flex max-md:flex-col max-md:items-center max-md:gap-4 max-md:w-full">
          {feeItems.map((item) => (
            <FeeCard key={item.label} {...item} />
          ))}
          <AdditionalFeesCard />
          <PricingBox />
          <PayBadge />
        </div>
      </div>
    </section>
  );
};

export default FeesSection;
